import { Button, Spinner } from '@mm/components';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { Drawer, Tooltip } from 'antd';
import { ReactComponent as MagicPencilIcon } from 'assets/icons/magic-pen.svg';
import React, { useEffect, useRef, useState } from 'react';
import EmailEditor, { EditorRef } from 'react-email-editor';
import { useSelector } from 'react-redux';
import { ReactComponent as HtmlEditorEmptyState } from '../svg/html-editor-empry-state.svg';
import css from './HtmlEditor.module.css';

import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useEmailTemplatesActions } from '@mm/state/actions/EmailTemplatesActions';
import {
  getEmailTemplates,
  getEmailTemplatesLoading,
} from '@mm/state/selectors/emailTemplates';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { useEmailWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-email-widget-handlers';
import { EmailTemplatesModal } from './EmailTemplatesModal';

type Props = {
  stateAndHandlers: ReturnType<typeof useEmailWidgetHandlers>;
  emailSendsCount: number;
};

export const HtmlEditor = ({ stateAndHandlers, emailSendsCount }: Props) => {
  const { bodyJsonValue, setBodyJsonValue, bodyDdValue, setBodyDdValue } =
    stateAndHandlers;

  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  const emailEditorRef = useRef<EditorRef>(null);

  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const signature = useSelector(activeFacebookPageSelector)?.unlayerHmac;

  const { retrievePage } = useConnectedPageCRUDActions();
  const emailTemplatesActions = useEmailTemplatesActions();
  const emailTemplates = useSelector(getEmailTemplates);
  const emailTemplatesLoading = useSelector(getEmailTemplatesLoading);

  useEffect(() => {
    if (!signature && facebookPageId) {
      retrievePage(facebookPageId, false, false, false, false, true);
    }
  }, [facebookPageId]);

  const onLoad = () => {
    if (bodyJsonValue) {
      emailEditorRef?.current?.editor?.loadDesign(bodyJsonValue);
    }
  };

  const onReady = () => {
    setShowLoadingIndicator(false);
  };

  const handleChooseTemplate = () => {
    setShowTemplatesModal(true);
    if (!emailTemplates.length) {
      emailTemplatesActions.retrieveAll();
    }
  };

  const handleTemplateSelect = template => {
    emailEditorRef.current?.editor?.loadDesign(template.bodyJson);
    setShowTemplatesModal(false);
  };

  const html = bodyDdValue || '';

  return !showHtmlEditor ? (
    <div className={css.container}>
      <div className={css.htmlEditButtonContainer}>
        <Button
          css={{ container: css.editHtmlButton }}
          leftIcon={<MagicPencilIcon />}
          onClick={() => {
            setShowHtmlEditor(true);
            setShowLoadingIndicator(true);
          }}
        >
          Edit Email
        </Button>
      </div>

      {html ? (
        <iframe title="email-preview" srcDoc={html} className={css.container} />
      ) : (
        <div className={css.emptyState}>
          <HtmlEditorEmptyState />
        </div>
      )}
    </div>
  ) : (
    <Drawer
      open={showHtmlEditor}
      width="100%"
      zIndex={2000002}
      onClose={() => {
        setShowHtmlEditor(false);
        setShowLoadingIndicator(false);
      }}
      title={
        <div className={css.htmlEditTitle}>
          <Tooltip
            title={
              false
                ? null
                : 'You cannot choose a template after you start sending'
            }
          >
            <div>
              <Button
                disabled={!!emailSendsCount}
                color="secondary-blue"
                css={{ container: css.saveButton }}
                onClick={handleChooseTemplate}
              >
                Choose Template
              </Button>
            </div>
          </Tooltip>
          <Button
            css={{ container: css.saveButton }}
            onClick={() => {
              if (emailEditorRef.current?.editor) {
                emailEditorRef.current?.editor?.exportHtml(data => {
                  const { design, html } = data;

                  setBodyJsonValue(design);
                  setBodyDdValue(html);
                  setShowHtmlEditor(false);
                  setShowLoadingIndicator(false);
                });
              } else {
                setShowHtmlEditor(false);
                setShowLoadingIndicator(false);
              }
            }}
          >
            Save
          </Button>
        </div>
      }
    >
      {showLoadingIndicator && <Spinner show backgroundColor="transparent" />}
      {signature && (
        <EmailEditor
          ref={emailEditorRef}
          onLoad={onLoad}
          onReady={onReady}
          options={{
            id: 'editor',
            projectId: UNLAYER_PROJECT_ID,
            displayMode: 'email',
            user: {
              id: facebookPageId, // Note: we want uploaded images to be available to people who have access to the page
              signature,
            },
            appearance: {
              theme: 'modern_light',
              panels: {
                tools: {
                  dock: 'left',
                },
              },
            },
          }}
          style={{ height: '85vh' }}
        />
      )}
      <EmailTemplatesModal
        visible={showTemplatesModal}
        templates={emailTemplates}
        loading={emailTemplatesLoading}
        onSelect={handleTemplateSelect}
        onClose={() => setShowTemplatesModal(false)}
      />
    </Drawer>
  );
};
